import api from '../client'
import { uploadMediaAsset } from '../mediaUpload'

export const commandCenterApi = {
  // Post Groups
  createPostGroup: async (data) => {
    const response = await api.post('/command-center/v2/post-groups', data)
    return response.data
  },
  // An edit's preflight names its distributed post, so a link or caption
  // label its books' posts already publish is not a conflict.
  preflightPostGroup: async (data, groupId) => {
    const config = groupId ? { params: { group_id: groupId } } : undefined
    const response = await api.post('/command-center/v2/post-groups/preflight', data, config)
    return response.data
  },
  listPostGroups: async (params = {}) => {
    const response = await api.get('/command-center/v2/post-groups', { params })
    return response.data
  },
  getPostGroup: async (id) => {
    const response = await api.get(`/command-center/v2/post-groups/${id}`)
    return response.data
  },
  updatePostGroup: async (id, updates) => {
    const response = await api.patch(`/command-center/v2/post-groups/${id}`, updates)
    return response.data
  },
  deletePostGroup: async (id, expectedRevision) => {
    const response = await api.delete(`/command-center/post-groups/${id}`, {
      params: { expected_revision: expectedRevision },
    })
    return response.data
  },
  undoPendingDeletePostGroup: async (id, expectedRevision) => {
    const response = await api.post(`/command-center/post-groups/${id}/undo-delete`, null, {
      params: { expected_revision: expectedRevision },
    })
    return response.data
  },
  approvePostGroup: async (id, expectedRevision) => {
    const response = await api.post(`/command-center/post-groups/${id}/approve`, {
      expected_revision: expectedRevision,
    })
    return response.data
  },
  rejectPostGroup: async (id, expectedRevision, reason) => {
    const response = await api.post(`/command-center/post-groups/${id}/reject`, {
      expected_revision: expectedRevision,
      reason,
    })
    return response.data
  },
  archivePostGroup: async (id, expectedRevision, reason) => {
    const response = await api.post(`/command-center/post-groups/${id}/archive`, {
      expected_revision: expectedRevision,
      reason,
    })
    return response.data
  },
  restorePostGroup: async (id, expectedRevision) => {
    const response = await api.post(`/command-center/post-groups/${id}/restore`, {
      expected_revision: expectedRevision,
    })
    return response.data
  },

  // Cross-org data
  listAllPosts: async (params = {}) => {
    const response = await api.get('/command-center/posts', { params })
    return response.data
  },
  listProviders: async () => {
    const response = await api.get('/command-center/providers')
    return response.data
  },
  listOrganizations: async (params = {}) => {
    const response = await api.get('/command-center/organizations', { params })
    return response.data
  },
  searchCreators: async (params = {}) => {
    const response = await api.get('/command-center/creators/search', { params })
    return response.data
  },
  listCreators: async (params = {}) => {
    const response = await api.get('/command-center/creators', { params })
    return response.data
  },
  createCreator: async (data) => {
    const response = await api.post('/command-center/creators', data)
    return response.data
  },
  updateCreator: async (id, data) => {
    const response = await api.patch(`/command-center/creators/${id}`, data)
    return response.data
  },
  deleteCreator: async (id) => {
    const response = await api.delete(`/command-center/creators/${id}`)
    return response.data
  },
  archiveCreator: async (id) => {
    const response = await api.post(`/command-center/creators/${id}/archive`)
    return response.data
  },
  uploadCreatorAvatar: async (id, file) => {
    const form = new FormData()
    form.append('avatar', file)
    const response = await api.post(`/command-center/creators/${id}/avatar`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },
  generateUploadUrl: async (contentType) => {
    const response = await api.post('/command-center/upload-url', { content_type: contentType })
    return response.data
  },
  listFeedOpsQueue: async (params = {}) => {
    const response = await api.get('/command-center/feed-ops/queue', { params })
    return response.data
  },
  getFeedOpsPost: async (postId, params) => {
    const response = await api.get(`/command-center/feed-ops/posts/${postId}`, { params })
    return response.data
  },
  createBulkCommand: async (data) => {
    const response = await api.post('/command-center/feed-ops/bulk-commands', data)
    return response.data
  },

  // Command Center media library
  listMediaAssets: async (params = {}) => {
    const response = await api.get('/command-center/media-assets', { params })
    return response.data
  },
  uploadMediaAsset: async (file, fields = {}, options = {}) => {
    return uploadMediaAsset(api, `/command-center/media-assets/uploads`, file, fields, options)
  },
  deleteMediaAsset: async (assetId) => {
    const response = await api.delete(`/command-center/media-assets/${assetId}`)
    return response.data
  },

  // Cross-org helpers that bypass the org slug rewriting interceptor
  listOrgTags: async (orgSlug, activeOnly = false) => {
    const response = await api.get(`/${orgSlug}/tags`, { params: { active_only: activeOnly }, _skipOrgRewrite: true })
    return response.data
  },
  listOrgSurfaces: async (orgSlug) => {
    const response = await api.get(`/${orgSlug}/surfaces`, { _skipOrgRewrite: true })
    return response.data
  },
  listOrgMediaAssets: async (orgSlug, params = {}) => {
    const response = await api.get(`/${orgSlug}/media-assets`, { params, _skipOrgRewrite: true })
    return response.data
  },
  startOrgMediaIngest: async (orgSlug, payload) => {
    const response = await api.post(`/${orgSlug}/media/ingest`, payload, { _skipOrgRewrite: true })
    return response.data
  },
  getOrgMediaIngestStatus: async (orgSlug, assetId) => {
    const response = await api.get(`/${orgSlug}/media/ingest/${assetId}`, { _skipOrgRewrite: true })
    return response.data
  },
  getAiCapabilities: async () => {
    const response = await api.get('/command-center/ai/capabilities')
    return response.data
  },
  suggestCaption: async (input) => {
    const payload = typeof input === 'string' ? { text: input } : { ...input }
    const response = await api.post('/command-center/ai/suggest-caption', payload)
    return response.data
  },
  suggestTags: async (text) => {
    const response = await api.post('/command-center/ai/suggest-tags', { text })
    return response.data
  },
  extractBetDetails: async (text) => {
    const response = await api.post('/command-center/ai/extract-bet-details', { text })
    return response.data
  },
  createTranscriptionJob: async (payload) => {
    const response = await api.post('/command-center/ai/jobs/transcription', payload)
    return response.data
  },
  createImageJob: async (payload) => {
    const response = await api.post('/command-center/ai/jobs/image', payload)
    return response.data
  },
  getAiJob: async (jobId) => {
    const response = await api.get(`/command-center/ai/jobs/${jobId}`)
    return response.data
  },
  retryAiJob: async (jobId) => {
    const response = await api.post(`/command-center/ai/jobs/${jobId}/retry`, undefined)
    return response.data
  },
  // A video analysis job a draft saved before Command Center had its own AI
  // key belongs to that draft's brand, so it is followed there.
  getOrgAiJob: async (orgSlug, jobId) => {
    const response = await api.get(`/${orgSlug}/ai/jobs/${jobId}`, { _skipOrgRewrite: true })
    return response.data
  },
  retryOrgAiJob: async (orgSlug, jobId) => {
    const response = await api.post(`/${orgSlug}/ai/jobs/${jobId}/retry`, undefined, { _skipOrgRewrite: true })
    return response.data
  },
  getOrgBranding: async (orgId) => {
    const response = await api.get(`/organizations/${orgId}/branding`)
    return response.data
  },
  // Leg search across destinations: each destination is searched as its own
  // organization and the answers merge into one /legs/search response.
  searchKambiLegsV2: async (providerId, { query, destinations, eventStatus, limit, cursor, searchSession, searchSeq }, { signal } = {}) => {
    const response = await api.post(`/command-center/v2/providers/${providerId}/kambi/legs/search`, {
      query,
      destinations,
      event_status: eventStatus,
      limit,
      cursor,
      search_session: searchSession,
      search_seq: searchSeq,
    }, { signal })
    return response.data
  },
  validateKambiOutcomesV2: async (providerId, outcomeIds, destinations) => {
    const response = await api.post(`/command-center/v2/providers/${providerId}/kambi/validate-outcomes`, {
      outcome_ids: outcomeIds,
      destinations,
    })
    return response.data
  },
  validateKambiOutcomes: async (orgSlug, outcomeIds, offeringIds) => {
    const response = await api.post(`/${orgSlug}/kambi/validate-outcomes`, {
      outcome_ids: outcomeIds,
      offering_ids: offeringIds,
    }, { _skipOrgRewrite: true })
    return response.data
  },
  getKambiOfferings: async (orgSlug) => {
    const response = await api.get(`/${orgSlug}/kambi/offerings`, { _skipOrgRewrite: true })
    return response.data
  },
  listOrgSelections: async (orgSlug, params = {}) => {
    const response = await api.get(`/${orgSlug}/selections/available`, { params, _skipOrgRewrite: true })
    return response.data
  },
  getOGEventContracts: async (orgSlug, eventId) => {
    const response = await api.get(`/${orgSlug}/og/events/${encodeURIComponent(eventId)}/contracts`, { _skipOrgRewrite: true })
    return response.data
  },
  searchOGPredictions: async (orgSlug, query, limit = 20) => {
    const response = await api.get(`/${orgSlug}/og/search/predictions`, {
      params: { q: query, limit },
      _skipOrgRewrite: true,
    })
    return response.data
  },
}
